import { Bounds } from '@ardoq/graph';
import {
  BlocksReference,
  BlocksNode,
  BlocksVisualState,
  ItemSet,
} from './types';
import { isReference, isNode, setVisualState } from './util';

function* nodes(set: Set<BlocksNode | BlocksReference>): Generator<BlocksNode> {
  for (const item of set.values()) {
    if (isNode(item)) {
      yield item as BlocksNode;
    }
  }
}

function* links(
  set: Set<BlocksNode | BlocksReference>
): Generator<BlocksReference> {
  for (const item of set.values()) {
    if (isReference(item)) {
      yield item;
    }
  }
}

export const createItemSet = (visualState: BlocksVisualState): ItemSet => {
  const set = new Set<BlocksNode | BlocksReference>();
  let node: BlocksNode | null | undefined; // = undefined;
  let link: BlocksReference | null | undefined; // = undefined;

  return {
    add: (item: BlocksNode | BlocksReference) => {
      setVisualState(item, visualState, true);

      set.add(item);

      if (!node && isNode(item)) {
        node = item as BlocksNode;
      }

      if (!link && isReference(item)) {
        link = item as BlocksReference;
      }
    },

    delete: (item: BlocksNode | BlocksReference) => {
      setVisualState(item, visualState, false);

      set.delete(item);

      if (item === node) {
        node = undefined;
      }

      if (item === link) {
        link = undefined;
      }
    },

    has: (item: BlocksNode | BlocksReference) => set.has(item),

    clear: () => {
      for (const item of set.values()) {
        setVisualState(item, visualState, false);
      }

      set.clear();
      node = null;
      link = null;
    },

    values: () => set.values(),

    get size() {
      return set.size;
    },

    isSubsetOf: (itemSet: ItemSet) => {
      for (const item of set.values()) {
        if (!itemSet.has(item)) {
          return false;
        }
      }

      return true;
    },

    node: () => {
      if (node === undefined) {
        const iterator = nodes(set).next();

        node = iterator.done ? null : iterator.value;
      }

      return node;
    },

    nodes: () => nodes(set),

    link: () => {
      if (link === undefined) {
        const iterator = links(set).next();

        link = iterator.done ? null : iterator.value;
      }

      return link;
    },

    links: () => links(set),

    bounds: () => {
      const bounds: Bounds = [+Infinity, +Infinity, -Infinity, -Infinity];

      for (const item of set.values()) {
        if (item.bounds) {
          bounds[0] = Math.min(bounds[0], item.bounds[0]);
          bounds[1] = Math.min(bounds[1], item.bounds[1]);
          bounds[2] = Math.max(bounds[2], item.bounds[2]);
          bounds[3] = Math.max(bounds[3], item.bounds[3]);
        }
      }

      return bounds;
    },
  };
};
